import { S3_BUCKET_URL } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { useAccount } from '@/hooks/useAccount';
import { useArchetypeCalculation } from '@/hooks/useArchetypeCalculation.ts';
import { webCampaign } from '@/modules/webCampaign';
import { useForestLayoutSelections } from '@/pages/Onboarding/hooks/useForestLayoutSelections';
import { useLayoutSelections } from '@/pages/Onboarding/hooks/useLayoutSelections';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { useTransBotInfo } from '@/pages/Onboarding/Layouts/OwnAI_Trans/hooks/useTransBotInfo';
import { useRoleplayRoomGirlImage } from '@/pages/Onboarding/Layouts/OwnAIRoleplayRooms/hooks/useRoleplayRoomGirlImage';
import { MainImageHeader } from '@/pages/Paywall/components/MainImageHeader';
import { PersonalizedHeader } from '@/pages/Paywall/components/PersonalizedHeader';
import { PaywallProFeatures } from '@/pages/Paywall/components/ProFeatures';
import {
  PLANS_ACCESS,
  SAFE_PLANS_ACCESS,
  VIP_PLANS_ACCESS,
} from '@/pages/Paywall/components/ProFeatures/constant';

import {
  MainImageContentStyles,
  MainImageStyles,
  TitleHighlightAlt,
} from './MainImage.styles';

const EXTRA_HEIGHT_PAYWALL_NOTES = [
  PAYWALL_NOTES.FOREST_IMAGES,
  PAYWALL_NOTES.POOL_GIRL_IMAGE,
];

export const SectionMainImage: React.FC = () => {
  const { ageGeneration, bodyShape, hairColor } = useLayoutSelections();
  const { bodyShape: forestBodyShape } = useForestLayoutSelections();
  const { user } = useAccount();

  const { imageUrl: transImageUrl } = useTransBotInfo('paywall');

  const { archetype } = useArchetypeCalculation();

  const backgroundImage = useRoleplayRoomGirlImage(true);

  const getBackgroundSrc = () => {
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.POOL_GIRL_IMAGE)
    ) {
      return `${S3_BUCKET_URL}/paywall/pool-girl/paywall.png`;
    }

    if (webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.FOREST_IMAGES)) {
      return `${S3_BUCKET_URL}/paywall/forest/female/${forestBodyShape}.png`;
    }

    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.ROLEPLAY_ROOM_GIRLS)
    ) {
      return backgroundImage;
    }
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.ARCHETYPE) ||
      webCampaign.setup.paywallNotes.includes(
        PAYWALL_NOTES.ARCHETYPE_REAL_PHOTOS,
      )
    ) {
      return archetype.paywallImage;
    }

    if (
      webCampaign.setup.paywallNotes.includes(
        PAYWALL_NOTES.REAL_GIRLS_PHOTOS_V2,
      )
    ) {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab/real_girls_photos_v3/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    }

    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.REAL_GIRLS_PHOTOS)
    ) {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab/real_girls_photos_v2/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    }

    if (webCampaign.setup.paywallImagesVariant === 'skinny_regular_on_bed') {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab/skinny_regular_on_bed/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    }

    if (
      webCampaign.setup.paywallImagesVariant === 'v6-sexy' ||
      webCampaign.setup.paywallNotes.includes('4-nsfw-girl')
    ) {
      return `${S3_BUCKET_URL}/paywall/v6-sexy/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    }

    if (webCampaign.setup.paywallImagesVariant === 'one-night-stand') {
      return `${S3_BUCKET_URL}/paywall/one-night-stand/paywall.png`;
    }

    if (webCampaign.setup.paywallImagesVariant === 'transsexual') {
      return transImageUrl;
    }

    return `${S3_BUCKET_URL}/paywall/v6/${ageGeneration}_${hairColor}_${bodyShape}.png`;
  };

  const getPlansAccess = () => {
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.VIP_PLANS_ACCESS)
    ) {
      return VIP_PLANS_ACCESS;
    }
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.SAFE_PLANS_ACCESS)
    ) {
      return SAFE_PLANS_ACCESS;
    }
    return PLANS_ACCESS;
  };

  const DefaultHeader = () => (
    <OwnAiTitle className="main-image-title">
      Get your plan and enjoy
      <br />
      <OwnAiTitleHighlight>unlimited</OwnAiTitleHighlight> chats
    </OwnAiTitle>
  );

  const OneNightStandHeader = () => (
    <OwnAiTitle className="main-image-title">
      Live the night of your
      <br />
      <OwnAiTitleHighlight>dreams...</OwnAiTitleHighlight>
    </OwnAiTitle>
  );

  const PoolGirlHeader = () => (
    <OwnAiTitle className="main-image-title">
      She's already waiting inside.
      <br />
      <OwnAiTitleHighlight>Unlock the connection.</OwnAiTitleHighlight>
    </OwnAiTitle>
  );

  const ForestHeader = () => {
    const username = user?.name || '';
    return (
      <OwnAiTitle className="main-image-title">
        The deeper you go{username ? ',' : '.'}
        {username && (
          <>
            <br />
            {username}.
          </>
        )}
        <br />
        <TitleHighlightAlt>the closer it feels.</TitleHighlightAlt>
      </OwnAiTitle>
    );
  };

  const getTitle = () => {
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.PERSONALIZED_HEADER)
    ) {
      return <PersonalizedHeader />;
    }
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.POOL_GIRL_IMAGE)
    ) {
      return <PoolGirlHeader />;
    }
    if (webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.FOREST_TITLE)) {
      return <ForestHeader />;
    }
    if (webCampaign.setup.paywallNotes.includes('one-night-stand')) {
      return <OneNightStandHeader />;
    }
    return <DefaultHeader />;
  };

  const hasExtraHeight = EXTRA_HEIGHT_PAYWALL_NOTES.some((note) =>
    webCampaign.setup.paywallNotes.includes(note),
  );

  return (
    <MainImageStyles
      backgroundSrc={getBackgroundSrc()}
      isScalable={webCampaign.setup.paywallNotes.includes(
        PAYWALL_NOTES.SCALABLE_PAYWALL_IMAGE,
      )}
      extraHeight={hasExtraHeight ? 140 : 0}
    >
      <MainImageHeader />
      <MainImageContentStyles>
        {getTitle()}

        <PaywallProFeatures features={getPlansAccess()} />
      </MainImageContentStyles>
    </MainImageStyles>
  );
};
